import { useEffect, useState } from "react";
import {
  LANGUAGE_MODEL_NONE,
  LANGUAGE_MODEL_PAIR,
  LANGUAGE_MODEL_SINGLE,
} from "utils/constantValues";

function useEditService({ defaultServices = [], defaultServicesRates = {} }) {
  const [activeService, setActiveService] = useState([]);
  const [serviceRates, setServiceRates] = useState({});

  function buildLangPairKey(fromLanguage, toLanguage) {
    const fromKey = fromLanguage ? String(fromLanguage) : "none";
    const toKey = toLanguage ? String(toLanguage) : "none";
    return `${fromKey}-${toKey}`;
  }

  const removeLanguage = (serviceId, langPairKey, setValue = () => {}) => {
    setServiceRates((prev) => {
      const next = { ...prev, [serviceId]: { ...prev[serviceId] } };
      delete next[serviceId][langPairKey];
      setValue("serviceRates", next);
      return next;
    });
  };

  const addLanguages = (service, langPairIndex, setValue = () => {}) => {
    const newLangPerKey = `${langPairIndex + 1}-${langPairIndex + 1}`;

    const newServiceRate = addNewServiceRate(service, {
      fromLanguage: "",
      toLanguage: "",
    });

    setValue("serviceRates", {
      ...serviceRates,
      [service.id]: {
        ...serviceRates[service.id],
        [newLangPerKey]: newServiceRate,
      },
    });

    setServiceRates((prevState) => ({
      ...prevState,
      [service.id]: {
        ...prevState[service.id],
        [newLangPerKey]: newServiceRate,
      },
    }));
  };

  function addNewServiceRate(service = {}, langPair = {}) {
    const serviceType = service.type.map((el, index) => {
      const languages =
        service.languageModel === LANGUAGE_MODEL_PAIR
          ? {
              fromLanguage: langPair.fromLanguage ?? "",
              toLanguage: langPair.toLanguage ?? "",
            }
          : service.languageModel === LANGUAGE_MODEL_SINGLE
          ? {
              toLanguage: langPair.toLanguage ?? "",
            }
          : {};

      return {
        rate: 0,
        rateId: el.id,
        title: el.title,
        capacity: 0,
        serviceName: service.title,
        serviceId: service.id,
        hasMoreThanRate: service.type.length > 1,
        ratesLengthPerService: service.type.length,
        withRate: service.withRate ?? true,
        withCapacity: service.withCapacity ?? false,
        CsLinguistSupportedServices:
          service.CsLinguistSupportedServices ?? false,
        isPair: service.languageModel === LANGUAGE_MODEL_PAIR,
        hasLanguages:
          service.languageModel !== LANGUAGE_MODEL_NONE || index > 1,
        languageModel: service.languageModel ?? LANGUAGE_MODEL_NONE,
        ...languages,
      };
    });

    return serviceType;
  }

  const onHandleLanguageChange = (
    newValue,
    serviceId,
    filedName = "",
    langPairKey,
    setValue = () => {},
    unregister = () => {}
  ) => {
    const currentRates = serviceRates[serviceId][langPairKey] || [];

    currentRates.forEach((rate, rateIndex) => {
      unregister(
        `rate-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
      );
      unregister(
        `capacity-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
      );
    });

    const updatedRates = currentRates.map((el) => ({
      ...el,
      [filedName]: newValue,
      rate: "",
      capacity: "",
    }));

    setServiceRates((prevState) => ({
      ...prevState,
      [serviceId]: { ...prevState[serviceId], [langPairKey]: updatedRates },
    }));

    updatedRates.forEach((rate, rateIndex) => {
      setValue(
        `rate-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`,
        ""
      );
      setValue(
        `capacity-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`,
        ""
      );
    });

    setValue("serviceRates", {
      ...serviceRates,
      [serviceId]: { ...serviceRates[serviceId], [langPairKey]: updatedRates },
    });
  };

  const onHandleRateInputChange = (
    newValue,
    serviceId,
    langPairKey,
    filedName = "",
    rateId,
    setValue = () => {}
  ) => {
    const currentServiceRates = serviceRates[serviceId][langPairKey];
    const updatedServiceRate = currentServiceRates.map((rate) => {
      if (rate.rateId === rateId) {
        return { ...rate, [filedName]: newValue };
      }
      return rate;
    });

    setServiceRates((prevState) => ({
      ...prevState,
      [serviceId]: {
        ...prevState[serviceId],
        [langPairKey]: updatedServiceRate,
      },
    }));

    setValue("serviceRates", {
      ...serviceRates,
      [serviceId]: {
        ...serviceRates[serviceId],
        [langPairKey]: updatedServiceRate,
      },
    });
  };

  const handleLangPairForTheFirstTIme = () => {
    const langPairKey = "0-0";
    return langPairKey;
  };

  const handleServiceRates = (service = {}, ratesTypes = {}) => {
    const langPairKeyFirstTime = handleLangPairForTheFirstTIme();

    if (!ratesTypes[service.id]) {
      ratesTypes[service.id] = {
        [langPairKeyFirstTime]: [],
      };
    }

    const defaultRatesForService =
      defaultServicesRates?.[service.id]?.[langPairKeyFirstTime] || [];

    const firstRate = defaultRatesForService[0] || {};

    const serviceType = addNewServiceRate(service, {
      fromLanguage: firstRate.fromLanguage || "",
      toLanguage: firstRate.toLanguage || "",
    });

    ratesTypes[service.id][langPairKeyFirstTime] = [
      ...ratesTypes[service.id][langPairKeyFirstTime],
      ...serviceType,
    ];

    return ratesTypes;
  };

  const removeService = (
    service = {},
    setValue = () => {},
    unregister = () => {}
  ) => {
    const filteredServices = activeService.filter(
      (elem) => elem.id !== service.id
    );

    Object.keys(serviceRates).forEach((serviceId) => {
      const langPairIds = Object.keys(serviceRates[serviceId] || {});
      langPairIds.forEach((langPairKey) => {
        const serviceRate = serviceRates[serviceId][langPairKey] || [];
        serviceRate.forEach((rate, rateIndex) => {
          if (service.id === serviceId) {
            unregister(`${serviceId}-${langPairKey}-from`);
            unregister(`${serviceId}-${langPairKey}-to`);
            unregister(
              `rate-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
            );
            unregister(
              `capacity-${serviceId}-${langPairKey}-${rateIndex}-${rate.rateId}-${rate.fromLanguage}-${rate.toLanguage}`
            );
          }
        });
      });
    });

    setValue(
      "services",
      filteredServices.map((elem) => elem.id)
    );

    setServiceRates((prev) => {
      const next = { ...prev };
      delete next[service.id];
      setValue("serviceRates", next);
      return next;
    });

    setActiveService(filteredServices);
  };

  const addService = (service = {}, setValue) => {
    const chosenService = [...activeService, service];

    const chosenServiceRates = handleServiceRates(service, serviceRates);

    setValue(
      "services",
      chosenService.map((elem) => elem.id)
    );

    setActiveService(chosenService);
    setServiceRates(chosenServiceRates);
    setValue("serviceRates", chosenServiceRates);
  };

  const getMaxPrice = ({
    isPair = false,
    languageFrom = {},
    languageTo = {},
    service = {},
  }) => {
    const ceilingPrices = service.ceilingPrices;

    if (isPair) {
      const languageFromId = languageFrom?.id;
      const languageToId = languageTo?.id;
      const hasCeilingPrices = ceilingPrices[languageFromId];

      if (hasCeilingPrices && languageToId) {
        return hasCeilingPrices[languageToId]
          ? hasCeilingPrices[languageToId]?.max ?? false
          : false;
      } else {
        return false;
      }
    } else {
      const languageToId = languageTo?.id;
      const hasCeilingPrices = ceilingPrices[languageToId];

      return hasCeilingPrices ? hasCeilingPrices?.max ?? false : false;
    }
  };

  function mergeExistingRatesWithGenerated(
    service,
    defaultRatesForService = []
  ) {
    const first = defaultRatesForService[0] || {};

    let fromLanguage = "";
    let toLanguage = "";

    if (service.languageModel === LANGUAGE_MODEL_PAIR) {
      fromLanguage = first.fromLanguage || "";
      toLanguage = first.toLanguage || "";
    } else if (service.languageModel === LANGUAGE_MODEL_SINGLE) {
      toLanguage = first.toLanguage || "";
    }

    const generated = addNewServiceRate(service, {
      fromLanguage,
      toLanguage,
    });

    return generated.map((rateType) => {
      const found = defaultRatesForService.find(
        (r) => r.rateId === rateType.rateId
      );

      if (found) {
        return {
          ...rateType,
          rate: found.rate ?? rateType.rate,
          capacity: found.capacity ?? rateType.capacity,
          fromLanguage: found.fromLanguage ?? rateType.fromLanguage,
          toLanguage: found.toLanguage ?? rateType.toLanguage,
        };
      }

      return rateType;
    });
  }

  const resetOnUnmount = () => {
    setActiveService([]);
    setServiceRates({});
  };

  useEffect(() => {
    const nextServiceRates = {};

    defaultServices.forEach((service) => {
      const serviceId = service.id;
      const existingLangPairs = defaultServicesRates[serviceId];

      if (!existingLangPairs) {
        const defaultKey =
          service.languageModel === LANGUAGE_MODEL_SINGLE ? "0-0" : "none-none";

        nextServiceRates[serviceId] = {
          [defaultKey]: mergeExistingRatesWithGenerated(service, []),
        };
        return;
      }

      nextServiceRates[serviceId] = {};

      Object.entries(existingLangPairs).forEach(([storedKey, rates]) => {
        let key = storedKey;

        if (service.languageModel === LANGUAGE_MODEL_PAIR) {
          const fromLang = rates[0]?.fromLanguage || "";
          const toLang = rates[0]?.toLanguage || "";
          key = buildLangPairKey(fromLang, toLang);
        }

        nextServiceRates[serviceId][key] = mergeExistingRatesWithGenerated(
          service,
          rates
        );
      });
    });

    setActiveService(defaultServices);
    setServiceRates(nextServiceRates);

    return () => resetOnUnmount();
  }, []);

  return {
    activeService,
    setActiveService,
    serviceRates,
    setServiceRates,
    handleServiceRates,
    removeService,
    addService,
    addLanguages,
    removeLanguage,
    onHandleLanguageChange,
    getMaxPrice,
    onHandleRateInputChange,
  };
}

export default useEditService;
